"use client";
import React, { useRef, useEffect, useMemo } from "react";
import { motion } from "motion/react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from "visuais";
import { IconPackage, IconShoppingCart } from "@tabler/icons-react";
import { useProdutosLogic as useProdutosLogicHook } from "./produtosLogic";
import {
  containerVariants,
  itemVariants,
  rowVariants,
  InfoCard,
} from "./shared";
import { ConexaoService, type ProdutoPedido } from "../logic/ConexaoService";

interface ProdutosHoverCardProps {
  produtos: ProdutoPedido[];
  pedidoObservacao?: string;
  isVisible: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export function ProdutosHoverCard({
  produtos,
  pedidoObservacao,
  isVisible,
  onMouseEnter,
  onMouseLeave,
}: ProdutosHoverCardProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // OTIMIZAÇÃO: Limitar o número de produtos para evitar travamentos
  const produtosLimitados = useMemo(() => {
    return produtos.slice(0, 50); // Limita a 50 produtos para performance no hover
  }, [produtos]);

  const {
    resumoCards,
    columns,
    produtosComObservacao,
    produtosSemConexao,
    conexaoStats,
    gerarChaveUnica,
  } = useProdutosLogicHook(produtosLimitados);

  // Função para capturar o scroll da roda do mouse
  const handleWheel = (e: WheelEvent) => {
    e.preventDefault();
    if (scrollRef.current) {
      scrollRef.current.scrollTop += e.deltaY;
    }
  };

  // Adicionar listener para o scroll da roda do mouse
  useEffect(() => {
    const element = scrollRef.current;
    if (element) {
      element.addEventListener("wheel", handleWheel, { passive: false });
      return () => {
        element.removeEventListener("wheel", handleWheel);
      };
    }
  }, []);

  // OTIMIZAÇÃO: Função otimizada para verificar conexão usando ConexaoService
  const temConexaoComXml = useMemo(() => {
    return (produto: ProdutoPedido) =>
      ConexaoService.isProductConnected(
        produto,
        conexaoStats.conexoesPossiveis
      );
  }, [conexaoStats.conexoesPossiveis]);

  const temMuitosProdutos = produtos.length > 50;

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0, x: 10, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 10, scale: 0.95 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="w-[450px] max-h-[500px] bg-popover border border-border rounded-lg shadow-lg p-3"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <motion.div
        ref={scrollRef}
        onClick={(e) => e.stopPropagation()}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-h-[450px] overflow-y-auto"
      >
        <motion.div
          className="flex items-center gap-2 mb-3"
          variants={itemVariants}
        >
          <div className="flex items-center justify-center bg-cyan-500 rounded p-0.5">
            <IconShoppingCart className="h-3 w-3 text-white" />
          </div>
          <span className="text-xs font-bold text-cyan-700 dark:text-cyan-300">
            Produtos ({produtos.length})
            {temMuitosProdutos && (
              <span className="text-[10px] text-orange-600 dark:text-orange-400 ml-1">
                (50 primeiros)
              </span>
            )}
          </span>
        </motion.div>

        {/* Observação do Pedido */}
        {pedidoObservacao && (
          <motion.div
            className="mb-3 p-2 bg-muted/30 rounded-md border-l-2 border-cyan-500"
            variants={itemVariants}
          >
            <div className="flex items-start gap-2">
              <span className="text-cyan-600 dark:text-cyan-400 text-xs">
                💬
              </span>
              <div className="flex-1">
                <span className="text-[10px] font-medium text-cyan-600 dark:text-cyan-400 uppercase tracking-wide">
                  Observação do Pedido
                </span>
                <p className="text-xs text-foreground/80 mt-0.5 leading-relaxed">
                  {pedidoObservacao}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Resumo dos Totais usando o InfoCard */}
        <motion.div
          className="grid grid-cols-3 gap-1 mb-3"
          variants={itemVariants}
        >
          {resumoCards.map((card, index) => (
            <InfoCard key={`resumo-${index}`} data={card} index={index} />
          ))}
        </motion.div>

        {/* Tabela de Produtos */}
        <motion.div
          variants={itemVariants}
          className="max-h-[200px] overflow-y-auto border border-border rounded-lg"
        >
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((column, index) => (
                  <TableHead key={index} className="text-[8px] font-medium">
                    {column.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {produtosLimitados.map((produto, index) => (
                <motion.tr
                  key={gerarChaveUnica(produto, index)}
                  variants={rowVariants}
                  className={cn(
                    "border-b border-border/50 hover:bg-muted/50 transition-colors text-[8px]",
                    temConexaoComXml(produto) &&
                      "bg-green-50 dark:bg-green-950/20",
                    produtosSemConexao.includes(produto) &&
                      "bg-red-50 dark:bg-red-950/20"
                  )}
                >
                  {columns.map((column, colIndex) => (
                    <TableCell key={colIndex} className="text-[7px] py-0.5">
                      {column.render
                        ? column.render(
                            produto[column.key as keyof ProdutoPedido],
                            produto
                          )
                        : String(
                            produto[column.key as keyof ProdutoPedido] || "-"
                          )}
                    </TableCell>
                  ))}
                </motion.tr>
              ))}
            </TableBody>
          </Table>
        </motion.div>

        {/* Informações adicionais */}
        {(produtosComObservacao.length > 0 ||
          produtosSemConexao.length > 0) && (
          <motion.div className="mt-4 space-y-2" variants={itemVariants}>
            {produtosComObservacao.length > 0 && (
              <div className="flex items-center gap-2 text-[10px]">
                <div className="flex items-center justify-center bg-blue-500 rounded p-0.5">
                  <IconPackage className="h-2.5 w-2.5 text-white" />
                </div>
                <span className="text-blue-700 dark:text-blue-300">
                  {produtosComObservacao.length} produtos com observação
                </span>
              </div>
            )}
            {produtosSemConexao.length > 0 && (
              <div className="flex items-center gap-2 text-[10px]">
                <div className="flex items-center justify-center bg-red-500 rounded p-0.5">
                  <IconPackage className="h-2.5 w-2.5 text-white" />
                </div>
                <span className="text-red-700 dark:text-red-300">
                  {produtosSemConexao.length} produtos sem conexão
                </span>
              </div>
            )}
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
}
